import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { SERVER_API } from './qlCalls'

const AuthContext = createContext()
/* eslint react/prop-types: 0 */
export const AuthProvider = ({ children }) => {
  // FIX 1: Initialize as null, not empty string
  const [user, setUser] = useState(null)
  const [sessionKey, setSessionKey] = useState(null)
  const [role, setRole] = useState(null)
  const [isCheckingSession, setIsCheckingSession] = useState(true)
  const navigate = useNavigate()

  const logout = useCallback(() => {
    setUser(null)
    setSessionKey(null)
    setRole(null)
    localStorage.removeItem('sessionKey')
    localStorage.removeItem('user')
    localStorage.removeItem('login')
    localStorage.removeItem('role')
    navigate('/', { replace: true })
  }, [navigate])

  // FIX 2: Create a function that accepts sessionKey as parameter to avoid closure issues
  const validateSessionWithKey = async (keyToValidate) => {
    console.log('Validating session with key:', keyToValidate ? 'exists' : 'missing')

    if (!keyToValidate) {
      console.log('No key provided, returning false')
      return false
    }

    try {
      const query = `query{checksession(sessionToken:\"${keyToValidate}\"){token}}`

      const getBody = (query, variables) => {
        return {
          OperationName: '',
          Query: query,
          Variables: variables,
        }
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

      const response = await fetch(SERVER_API, {
        method: 'POST',
        mode: 'cors',
        body: JSON.stringify(getBody(query)),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        console.log('Session validation failed, status:', response.status)
        return false
      }

      const json = await response.json()
      console.log('Session validation response:', json)

      // Check if session is valid
      if (json?.data?.checksession?.token) {
        console.log('Session is valid')
        return true
      }

      console.log('Session token not found in response')
      return false
    } catch (error) {
      console.error('Session validation error:', error)
      // Don't return false on network errors - assume session might still be valid
      // This prevents logout on temporary network issues
      if (error.name === 'AbortError') {
        console.log('Request timed out, assuming session still valid')
        return true // Assume valid on timeout
      }
      return false
    }
  }

  // Keep original function for backward compatibility
  const checkSessionValid = useCallback(async () => {
    return validateSessionWithKey(sessionKey)
  }, [sessionKey])

  // Initialize auth state from storage on mount
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('=== AUTH INITIALIZATION START ===')

      const storedSession = localStorage.getItem('sessionKey')
      const storedUser = localStorage.getItem('user')
      const storedRole = localStorage.getItem('role')

      console.log('Stored session:', storedSession ? 'exists' : 'missing')
      console.log('Stored user:', storedUser)
      console.log('Stored role:', storedRole)

      if (storedSession && storedUser) {
        // FIX 3: Validate BEFORE setting state, using the stored value directly
        const isValid = await validateSessionWithKey(storedSession)
        console.log('Session validation result:', isValid)

        if (isValid) {
          // Only set state if session is valid
          setSessionKey(storedSession)
          setUser(storedUser)
          setRole(storedRole)
          console.log('Session restored successfully')
        } else {
          // Clear invalid session
          console.log('Session invalid, clearing storage')
          localStorage.removeItem('sessionKey')
          localStorage.removeItem('user')
          localStorage.removeItem('login')
          localStorage.removeItem('role')
        }
      } else {
        console.log('No stored session found')
      }

      setIsCheckingSession(false)
      console.log('=== AUTH INITIALIZATION END ===')
    }

    initializeAuth()
  }, []) // Empty dependency array - only run once on mount

  // Periodic session validation (every 5 minutes)
  useEffect(() => {
    if (!user || !sessionKey) {
      return
    }

    console.log('Setting up periodic session validation')

    const validateSession = async () => {
      console.log('Running periodic session check')
      const isValid = await validateSessionWithKey(sessionKey)

      if (!isValid) {
        console.log('Session expired during periodic check, logging out')
        logout()
      }
    }

    // Don't check immediately - we already validated during init
    const intervalId = setInterval(validateSession, 5 * 60 * 1000)

    return () => {
      console.log('Clearing session validation interval')
      clearInterval(intervalId)
    }
  }, [user, sessionKey, logout])

  // Handle unauthorized events
  useEffect(() => {
    const handleUnauthorized = (event) => {
      if (event.detail?.status === 401) {
        console.log('Unauthorized access detected, logging out...')
        logout()
      }
    }

    window.addEventListener('unauthorized', handleUnauthorized)
    return () => window.removeEventListener('unauthorized', handleUnauthorized)
  }, [logout])

  const login = async (username, password, redirectTo = '/dashboard') => {
    const query = `query{login(username:\"${username}\",password:\"${password}\"){sessionToken{token}, activeRoleName, permissionPrivilege {permission, privilege} }}`

    const getBody = (query, variables) => {
      return {
        OperationName: '',
        Query: query,
        Variables: variables,
      }
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000)

    console.log('Attempting login for user:', username)

    const response = await fetch(SERVER_API, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify(getBody(query)),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    const json = await response.json()
    console.log('Login response:', json)

    if (!response.ok) {
      if (response.status === 400) {
        throw new Error('Wrong username or password')
      }
      alert('System error, please login later')
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = json

    if (data === null) {
      navigate('/')
      return
    }

    if (data.data.login === null) {
      alert('Wrong username and password')
      return
    }

    if (data.data.login.sessionToken?.token) {
      const token = data.data.login.sessionToken.token
      const roleName = data.data.login.activeRoleName

      // Set state
      setSessionKey(token)
      setRole(roleName)
      setUser(username)

      // Persist to localStorage
      localStorage.setItem('login', JSON.stringify(data.data))
      localStorage.setItem('role', roleName)
      localStorage.setItem('user', username)
      localStorage.setItem('sessionKey', token)

      console.log('Login successful, redirecting to:', redirectTo)
      alert('Login successfully')
      navigate(redirectTo, { replace: true })
    } else {
      console.log('No session token in response')
      alert('Session time out, please log out and log in again')
      logout()
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        sessionKey,
        role,
        login,
        logout,
        checkSessionValid,
        isCheckingSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  return useContext(AuthContext)
}
